// src/services/workHoursService.js

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { applyManualWorkHoursChanges } from './workHoursAudit';

/**
 * Sincronizza le ore corrette da una segnalazione con il sistema workHours
 * @param {Object} hoursData - Dati delle ore da sincronizzare
 * @param {string} hoursData.date - Data nel formato YYYY-MM-DD
 * @param {number} hoursData.standardHours - Ore standard
 * @param {number} hoursData.overtimeHours - Ore straordinario
 * @param {string} hoursData.notes - Note della correzione
 * @param {string} hoursData.userId - ID dell'utente
 * @returns {Promise<Object>} - Risultato della sincronizzazione
 */
export const syncWorkHoursForReport = async (hoursData) => {
  try {
    console.log('🔄 Inizio sincronizzazione workHours per segnalazione:', hoursData);
    
    const { date, standardHours, overtimeHours, notes, userId } = hoursData;
    
    // Validazione input
    if (!date || !userId) {
      throw new Error('Data e userId sono obbligatori');
    }
    
    // Validazione ore standard (numeri interi 0-8 o lettere speciali)
    if (standardHours !== undefined && standardHours !== '') {
      const value = standardHours.toString().toUpperCase();
      const isNumber = !isNaN(value) && value !== '';
      const isSpecialLetter = ['A', 'F', 'P', 'C', 'M'].includes(value);
      
      if (isNumber) {
        const numValue = parseFloat(value);
        if (!Number.isInteger(numValue)) {
          throw new Error('Le ore standard devono essere numeri interi (0, 1, 2, ..., 8)');
        }
        if (numValue < 0 || numValue > 8) {
          throw new Error('Le ore standard devono essere tra 0 e 8');
        }
      } else if (!isSpecialLetter) {
        throw new Error('Le ore standard devono essere un numero intero (0-8) o una lettera speciale (A, F, P, C, M)');
      }
      
      // Se è una lettera speciale, non deve esserci straordinario
      if (isSpecialLetter && overtimeHours && overtimeHours > 0) {
        throw new Error('Non è possibile inserire straordinari con lettere speciali');
      }
    }
    
    // Validazione ore straordinario (solo numeri interi)
    if (overtimeHours !== undefined && overtimeHours !== '') {
      const numValue = parseFloat(overtimeHours);
      if (isNaN(numValue)) {
        throw new Error('Le ore straordinario devono essere numeriche');
      }
      if (!Number.isInteger(numValue)) {
        throw new Error('Le ore straordinario devono essere numeri interi (0, 1, 2, ..., 12)');
      }
      if (numValue < 0 || numValue > 12) {
        throw new Error('Le ore straordinario devono essere tra 0 e 12');
      }
    }
    
    // Parsing della data
    const [year, month, day] = date.split('-');
    if (!year || !month || !day) {
      throw new Error('Formato data non valido. Utilizzare YYYY-MM-DD');
    }
    
    // Normalizza il mese (rimuovi zero iniziali)
    const normalizedMonth = month.replace(/^0+/, '') || month;
    
    // ID del documento workHours
    const docId = `${userId}_${normalizedMonth}_${year}`;
    const workHoursRef = doc(db, 'workHours', docId);
    
    console.log(`📄 Riferimento documento: ${docId}`);
    
    // Valori attuali del giorno, conservati come "valori precedenti" della correzione
    const workHoursSnap = await getDoc(workHoursRef);
    const originalEntry = workHoursSnap.exists()
      ? (workHoursSnap.data().entries || []).find(entry => entry.date === date)
      : null;

    const dayOfWeek = new Date(date).toLocaleDateString('it-IT', { weekday: 'long' });
    const isWeekend = [0, 6].includes(new Date(date).getDay());
    const isSpecialLetter = ['A', 'F', 'P', 'C', 'M'].includes(standardHours?.toString().toUpperCase());
    const correctionDate = new Date().toISOString();

    // La correzione è una modifica manuale: passa dal salvataggio tracciato, che
    // scrive solo questo giorno e annota autore, data e valori prima → dopo
    const result = await applyManualWorkHoursChanges({
      userId,
      month: normalizedMonth,
      year,
      changes: [{
        date,
        day: dayOfWeek,
        total: standardHours,
        overtime: isSpecialLetter ? 0 : (overtimeHours || 0),
        notes: notes || `Corretto da segnalazione del ${new Date(date).toLocaleDateString('it-IT')}`,
        isWeekend,
        extra: {
          correctedByReport: true, // Flag per identificare correzioni da segnalazioni
          lastCorrectionDate: correctionDate,
          ...(originalEntry ? {
            previousValues: {
              total: originalEntry.total ?? '',
              overtime: originalEntry.overtime ?? 0,
              notes: originalEntry.notes ?? '',
              correctedAt: correctionDate
            }
          } : {})
        }
      }],
      source: 'correzione-da-segnalazione'
    });

    console.log(`✅ Correzione workHours salvata: ${result.changedDays} giorno/i modificati`);

    return {
      success: true,
      action: originalEntry ? 'updated' : 'created',
      date,
      hours: {
        standard: standardHours,
        overtime: isSpecialLetter ? 0 : (parseInt(overtimeHours) || 0),
        total: isSpecialLetter ? standardHours : (parseInt(standardHours || 0) + parseInt(overtimeHours || 0)),
        isSpecialCode: isSpecialLetter
      },
      documentId: docId
    };
    
  } catch (error) {
    console.error('❌ Errore nella sincronizzazione workHours:', error);
    throw new Error(`Errore sincronizzazione: ${error.message}`);
  }
};

/**
 * Ottiene le ore attuali per una data specifica da workHours
 * @param {string} userId - ID dell'utente
 * @param {string} date - Data nel formato YYYY-MM-DD
 * @returns {Promise<Object|null>} - Entry trovata o null
 */
export const getCurrentWorkHoursForDate = async (userId, date) => {
  try {
    const [year, month] = date.split('-');
    const normalizedMonth = month.replace(/^0+/, '') || month;
    const docId = `${userId}_${normalizedMonth}_${year}`;
    
    const workHoursRef = doc(db, 'workHours', docId);
    const workHoursSnap = await getDoc(workHoursRef);
    
    if (!workHoursSnap.exists()) {
      return null;
    }
    
    const entries = workHoursSnap.data().entries || [];
    const entry = entries.find(e => e.date === date);
    
    return entry || null;
  } catch (error) {
    console.error('Errore nel recupero ore per data:', error);
    return null;
  }
};

/**
 * Verifica se un'entry è stata corretta da una segnalazione
 * @param {Object} entry - Entry da verificare
 * @returns {boolean} - True se corretta da segnalazione
 */
export const isCorrectedByReport = (entry) => {
  return entry?.correctedByReport === true;
};

/**
 * Ottiene lo storico delle correzioni per una data
 * @param {Object} entry - Entry da verificare
 * @returns {Array} - Array delle correzioni precedenti
 */
export const getCorrectionHistory = (entry) => {
  if (!entry || !entry.previousValues) {
    return [];
  }
  
  return [{
    correctedAt: entry.previousValues.correctedAt,
    previousTotal: entry.previousValues.total,
    previousOvertime: entry.previousValues.overtime,
    previousNotes: entry.previousValues.notes
  }];
};

/**
 * Valida i dati delle ore prima della sincronizzazione
 * @param {Object} hoursData - Dati da validare
 * @returns {Object} - Risultato della validazione
 */
export const validateHoursData = (hoursData) => {
  const errors = [];
  const { date, standardHours, overtimeHours, userId } = hoursData;
  
  // Validazioni base
  if (!userId) errors.push('ID utente mancante');
  if (!date) errors.push('Data mancante');
  
  // Validazione formato data
  if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    errors.push('Formato data non valido (richiesto: YYYY-MM-DD)');
  }
  
  // Validazione ore
  if (standardHours !== undefined) {
    const value = standardHours.toString().toUpperCase();
    const isNumber = !isNaN(value) && value !== '';
    const isSpecialLetter = ['A', 'F', 'P', 'C', 'M'].includes(value);
    
    if (isNumber) {
      const numValue = parseFloat(value);
      if (!Number.isInteger(numValue)) {
        errors.push('Ore standard non valide (solo numeri interi 0-8)');
      } else if (numValue < 0 || numValue > 8) {
        errors.push('Ore standard non valide (0-8)');
      }
    } else if (!isSpecialLetter) {
      errors.push('Ore standard non valide (0-8 o A/F/P/C/M)');
    }
  }
  
  if (overtimeHours !== undefined) {
    const numValue = parseFloat(overtimeHours);
    if (isNaN(numValue)) {
      errors.push('Ore straordinario non valide (solo numeri)');
    } else if (!Number.isInteger(numValue)) {
      errors.push('Ore straordinario non valide (solo numeri interi 0-12)');
    } else if (numValue < 0 || numValue > 12) {
      errors.push('Ore straordinario non valide (0-12)');
    }
  }
  
  // Validazione data nel passato (non più di 1 anno)
  if (date) {
    const inputDate = new Date(date);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    if (inputDate < oneYearAgo) {
      errors.push('Non è possibile correggere date più vecchie di un anno');
    }
    
    if (inputDate > new Date()) {
      errors.push('Non è possibile correggere date future');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};